import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import $ from "jquery";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import { useVisualizerStore } from "../useVisualizerStore";
import { dockStateStore, getVisualizerInstance } from "../../docking/dockStore";
import { DOCK_SLOTS } from "../../docking/dockTypes";
import { getCurrentLayout } from "../../appLayout";
import { getCoordsCenter } from "../../look/lookNavigation/lookFunctions";
import { getFeatureGroupCommonCoords } from "./getFeatureGroupCommonCoords";
import { shouldZoomPartId } from "../../look/hocs/withZoomToFeature";
import Misc from "../../../misc/misc";
import { isTouchDevice } from "../../../misc/utils";
import ActionTypes from "../../../misc/actionTypes";
import { useAppSelector } from "../../../storeHooks";
import type { CanvasSize } from "../useCanvasSize";
import {
  computeFocalTransform,
  getOverlayCenterOffset,
  getWrapperElement,
  getWrapperViewportRect,
  percentToFocalCoord,
} from "../canvasTransformMath";
import type { CenterOffset, Size } from "../canvasTransformMath";
import {
  FEATURE_ZOOM_ANIMATION_MS,
  FEATURE_ZOOM_EASING,
  getFeatureZoomScale,
  getFeatureZoomOffset,
  getFeatureZoomMatchesIdle,
  getIdlePanPercent,
} from "./featureGroupZoomDefaults";

// Toggle for testing whether waiting on DockedVisualizerWrapper's syncToViewport RAF loop (see
// waitForViewportSyncToFinish below) is actually needed before this hook applies its own
// setTransform. Flip to true locally to A/B the two behaviors; defaults to false since the wait
// may no longer be necessary. When false, the effect below calls its zoom logic immediately
// instead of deferring it via waitForViewportSyncToFinish.
const USE_VIEWPORT_WAIT = false;

/**
 * Extends this hook's feature-group framing to the FULLSCREEN visualizer slot (previously
 * materials-only). Flip to false to go back to "the fullscreen canvas is never moved for you",
 * leaving only manual panning — nothing else needs changing.
 *
 * On the fullscreen mobile/tablet layout the navigation menu is an overlay pinned to the right
 * edge of the same viewport the canvas fills, so framing also has to dodge it — see
 * FULLSCREEN_OVERLAY_SELECTOR and getOverlayCenterOffset.
 */
const ENABLE_FULLSCREEN_FEATURE_ZOOM = true;

/**
 * The open mobile navigation menu (scss/components/mobilemenu.scss) — the only thing that
 * overlays the fullscreen canvas. `.menu` is always in the DOM on mobile; `--opened` is what makes
 * it visible, and above the mobile breakpoint the whole element is display:none (which
 * getOverlayCenterOffset detects on its own, via offsetWidth === 0).
 */
const FULLSCREEN_OVERLAY_SELECTOR = ".menu.menu--opened";

/**
 * Below this canvas width (px, base/cover-scale — the same units as useCanvasSize's canvasSize)
 * the per-part feature zoom is disabled: the canvas is already so narrow that zooming in past
 * scale 1 crops away all the context around the framed group. The framing itself still happens —
 * see canScaleToFeature in the positioning effect — it just uses scale 1.
 */
const MIN_CANVAS_WIDTH_FOR_FEATURE_SCALE = 480;

/* -------------------------------------------------------------------------- *
 * Orientation flips (touch devices, embedded /materials slot)
 * -------------------------------------------------------------------------- *
 *
 * A real portrait<->landscape flip is the one case where this hook's normal
 * "just call setTransform when the deps change" approach cannot win, because
 * during the flip there is nothing to call it on and, right after it, two other
 * authorities reset the transform:
 *
 *   1. useResizingLoaderHook flags `isResizing` on a true flip, and
 *      DockedVisualizerWrapper renders a <LoadingSpinner/> instead of the
 *      <TransformWrapper> for ~200ms — so `transformRef.current` is null. The
 *      window resize also lands new windowWidth/windowHeight in redux, which
 *      changes `canvasSize` and re-runs the positioning effect below *while the
 *      ref is still null*: it bails out at the `if (!ref)` guard and the framing
 *      is simply lost.
 *   2. When the flip settles, `rwKey` bumps and <TransformWrapper> remounts
 *      fresh — `centerOnInit` fires from its own ResizeObserver callback (i.e.
 *      after every React effect of that commit) and cover-centers the canvas.
 *      DockedVisualizerWrapper's `syncToViewport` RAF loop then keeps
 *      cover-fitting it for another ~560ms from whenever `canvasSize` last
 *      changed, with zero-duration setTransform calls that would stomp any
 *      animation we started in the meantime.
 *
 * So instead of re-framing at the moment the deps change, we re-frame *after the
 * flip is finished*: wait out a short settle delay (long enough for the remount
 * in 1), then poll until the ref exists again and `isSyncingViewportRef` reads
 * false (i.e. the loop in 2 is done), and only then apply the zoom.
 *
 * ORIENTATION_REFRAME_WINDOW_MS is what makes this robust against *when* the
 * app's resize handler actually dispatches the new window size: for that long
 * after a flip, every re-run of the positioning effect (the orientation tick
 * itself, and any late canvasSize/minScale change that follows it) goes through
 * this deferred path instead of applying immediately, and each re-run cancels
 * the previous pending wait via the effect cleanup. After the window closes the
 * hook is back to its normal, immediate behavior.
 */
const ORIENTATION_SETTLE_MS = 250;
const ORIENTATION_REFRAME_WINDOW_MS = 1200;
const ORIENTATION_REFRAME_TIMEOUT_MS = 2000;

/**
 * Slots that get the deferred re-frame described above. Only the embedded
 * materials canvas needs it: the fullscreen slot is never re-framed on a resize
 * at all (see the guard in the positioning effect), and the cart slot has no
 * selector. Add a slot here to widen it.
 */
const ORIENTATION_REFRAME_SLOTS: string[] = [DOCK_SLOTS.materials];

/* -------------------------------------------------------------------------- *
 * Dock-slot switches (route changes, e.g. /shoppingcart -> /materials)
 * -------------------------------------------------------------------------- *
 *
 * Same class of problem as the orientation flip above, different trigger: when
 * the route changes, DockRouteSync flips `dockStateStore.activeSlotName`, which
 * re-runs the positioning effect below *immediately* — but at that moment the
 * dock box is still the OLD slot's box. VisualizerDockShell only measures the
 * new slot on the next animation frame (setRectNextFrame), then animates the
 * overlay to it over ~500ms, and DockedVisualizerWrapper's syncToViewport RAF
 * loop follows that animating box for TRANSITION_MS + buffer with zero-duration
 * `setTransform(..., 0)` calls every frame.
 *
 * So an immediate apply here is doubly wasted: it frames against the old
 * viewport, and then every frame of the sync loop overwrites it with a plain
 * cover-fit. The visible symptom is "the zoom just doesn't happen" after
 * navigating to /materials even though activePartId is set — which is exactly
 * what the loop leaves behind (the effect never re-runs afterwards, because
 * activePartId hasn't changed and minScale often lands on the same MIN_SCALE
 * floor for both slots, so nothing re-triggers it once the box has settled).
 *
 * Fix: for a short window after a slot switch, defer the framing the same way
 * the orientation path does — wait for the sync loop to start and finish (or
 * for a grace period, if it never starts), then apply against the settled box.
 *
 * Flip ENABLE_DOCK_SWITCH_REFRAME to false to go back to applying immediately.
 */
const ENABLE_DOCK_SWITCH_REFRAME = true;

/**
 * How long after a slot switch re-runs of the positioning effect still go
 * through the deferred path (the switch itself, plus the late minScale/
 * canvasSize change the new dock box produces a frame or two later). Covers the
 * shell's 500ms box transition plus syncToViewport's own buffer.
 */
const DOCK_SWITCH_REFRAME_WINDOW_MS = 900;

/**
 * How long to wait for syncToViewport's RAF loop to *start* before giving up on
 * it and applying anyway. Needed because the loop only starts once the new dock
 * rect has been measured (next animation frame at the earliest), i.e. after this
 * effect has already run — so `isSyncingViewportRef` still reads false when the
 * wait begins. Once the loop is seen running, this no longer applies and the
 * wait simply polls until it finishes.
 */
const DOCK_SWITCH_SYNC_START_GRACE_MS = 250;

/** Hard cap, so a never-settling sync loop can only delay the framing, never cancel it. */
const DOCK_SWITCH_REFRAME_TIMEOUT_MS = 2500;

type Orientation = "portrait" | "landscape";

/**
 * Options for one apply of the framing.
 *
 * `featureOnly` — apply ONLY if there is a real feature group to frame; skip entirely when the
 * result would be the idle/default position (no active feature, a part that isn't zoomed to, or a
 * part configured with matchIdle). Used by the deferred dock-switch path, where "nothing to zoom
 * to" should mean "leave the freshly cover-fit canvas alone" rather than "nudge it to idle after
 * the page has already settled".
 */
interface ApplyOptions {
  featureOnly?: boolean;
}

function getOrientation(): Orientation {
  if (typeof window === "undefined") {
    return "landscape";
  }
  return window.innerHeight > window.innerWidth ? "portrait" : "landscape";
}

/**
 * Waits out `settleMs`, then polls `isReady` once per animation frame and calls
 * `onReady` as soon as it returns true — or once `timeoutMs` has elapsed, so a
 * never-settling condition can only delay the re-frame, never cancel it (a late
 * apply that gets stomped is recoverable; no apply at all is not).
 *
 * @returns cancel function; call it on effect cleanup so a stale wait can't fire
 *   setTransform after the dependencies have moved on.
 */
function waitForOrientationSettle(
  settleMs: number,
  isReady: () => boolean,
  onReady: () => void,
  timeoutMs: number,
): () => void {
  let cancelled = false;
  let rafId = 0;
  const startedAt = Date.now();

  const poll = () => {
    if (cancelled) {
      return;
    }
    if (!isReady() && Date.now() - startedAt < timeoutMs) {
      rafId = requestAnimationFrame(poll);
      return;
    }
    onReady();
  };

  const timerId = setTimeout(() => {
    if (cancelled) {
      return;
    }
    rafId = requestAnimationFrame(poll);
  }, settleMs);

  return () => {
    cancelled = true;
    clearTimeout(timerId);
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
}

/**
 * Waits out a dock-slot switch: polls once per animation frame until
 * syncToViewport's RAF loop has been seen running and has then finished (or,
 * if it never starts within `startGraceMs`, until that grace period is up),
 * and until the transform ref exists — then calls `onReady`.
 *
 * `timeoutMs` is the same safety valve as waitForOrientationSettle's: a
 * never-settling condition can only delay the framing, never cancel it.
 *
 * @returns cancel function; call it on effect cleanup so a stale wait can't
 *   fire setTransform after the dependencies have moved on.
 */
function waitForDockSwitchToSettle(
  isSyncingViewportRef: React.RefObject<boolean> | undefined,
  isReady: () => boolean,
  onReady: () => void,
  startGraceMs: number,
  timeoutMs: number,
): () => void {
  let cancelled = false;
  let rafId = 0;
  let sawSyncStart = false;
  const startedAt = Date.now();

  const poll = () => {
    if (cancelled) {
      return;
    }

    const elapsed = Date.now() - startedAt;
    const isSyncing = !!(isSyncingViewportRef && isSyncingViewportRef.current);
    if (isSyncing) {
      sawSyncStart = true;
    }

    if (elapsed < timeoutMs) {
      // The loop hasn't started yet — it can't, until the new dock rect lands.
      // Keep waiting for it, but only for the grace period: on a re-run late in
      // the window (or a slot switch that doesn't change the box at all) there
      // may be no loop to wait for.
      if (!sawSyncStart && elapsed < startGraceMs) {
        rafId = requestAnimationFrame(poll);
        return;
      }
      // Running: wait it out, so our animated setTransform starts from the
      // settled position instead of being stomped frame by frame.
      if (isSyncing || !isReady()) {
        rafId = requestAnimationFrame(poll);
        return;
      }
    }

    onReady();
  };

  rafId = requestAnimationFrame(poll);

  return () => {
    cancelled = true;
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
}

/**
 * Rests the transform at the per-visualizerType (and, where configured, per-layout — see
 * byLayoutType on IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE) idle pan, at minScale. Shared by the
 * "nothing to frame" case and by any part/layout configured with matchIdle in
 * featureGroupZoomDefaults.ts, so both paths land on pixel-identical framing instead of two
 * independently-tuned approximations of the same spot.
 */
function applyIdleFraming(
  ref: ReactZoomPanPinchRef,
  canvasSize: CanvasSize,
  viewport: Size,
  minScale: number,
  visualizerType: string,
  layoutType: number | undefined,
  centerOffset: CenterOffset,
  animationMs: number,
) {
  const idlePercent = getIdlePanPercent(visualizerType, layoutType);
  const { positionX, positionY } = computeFocalTransform({
    contentWidth: canvasSize.width,
    contentHeight: canvasSize.height,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
    scale: minScale,
    focalX: percentToFocalCoord(idlePercent.x, canvasSize.width, viewport.width, minScale),
    focalY: percentToFocalCoord(idlePercent.y, canvasSize.height, viewport.height, minScale),
    centerOffsetX: centerOffset.x,
    centerOffsetY: centerOffset.y,
  });
  ref.setTransform(positionX, positionY, minScale, animationMs, FEATURE_ZOOM_EASING as any);
}

/**
 * Defers `onReady` until `isSyncingViewportRef.current` is falsy, polling once per animation
 * frame. Always waits at least one frame before its first check (even if the ref already reads
 * falsy) so a sibling effect that sets the ref in the *same* commit — but is declared/runs after
 * this hook's effect — has a chance to flip it first; see the effect ordering note where this is
 * called from.
 *
 * @returns cancel function; call on effect cleanup so a stale wait can't fire
 *   setTransform after the component/dependencies have moved on.
 */
function waitForViewportSyncToFinish(
  isSyncingViewportRef: React.RefObject<boolean> | undefined,
  onReady: () => void,
): () => void {
  let cancelled = false;
  let rafId: number;
  const poll = () => {
    if (cancelled) {
      return;
    }
    if (isSyncingViewportRef && isSyncingViewportRef.current) {
      rafId = requestAnimationFrame(poll);
      return;
    }
    onReady();
  };
  rafId = requestAnimationFrame(poll);
  return () => {
    cancelled = true;
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
}

/**
 * Maps a dock slot to the redux value naming its "currently active feature
 * group" (a partId string). The materials wizard already computes this (see
 * materialsWizardMain.js's setNewCanvasClass) and stores it as
 * `selectionState.materialsCanvasClass`.
 *
 * DOCK_SLOTS.visualizer (fullscreen) reads navigationState.visualizerCanvasPartId,
 * written by the mobile navigation. Everything below this map is slot-agnostic,
 * so adding a further slot is just another entry here.
 */
const ACTIVE_PART_ID_SELECTOR_BY_SLOT: Record<string, (state: any) => string | null> = {
  [DOCK_SLOTS.materials]: (state: any) => state.selectionState.materialsCanvasClass,
  // Fullscreen visualizer: written by visComponents/navigation/mobileNavigationCommon.js's
  // syncVisualizerCanvasPartId, at the same "active feature GROUP changed" granularity the
  // materials entry above gets from setNewCanvasClass/syncMaterialsCanvasClass. It is never
  // cleared on menu close, so closing the menu leaves the canvas untouched (see that action's
  // JSDoc in actions/navigationFunctions.js).
  ...(ENABLE_FULLSCREEN_FEATURE_ZOOM
    ? { [DOCK_SLOTS.visualizer]: (state: any) => state.navigationState.visualizerCanvasPartId }
    : {}),
};

/**
 * Zooms/pans the persistent docked canvas to frame whichever feature group is
 * "active" in the currently docked slot — the dynamic replacement for the old
 * per-part SCSS transforms (scss/bv/responsive-canvas-*.scss). Those only
 * worked when the canvas was a normal in-flow DOM node the wizard's CSS could
 * select directly; under the docked canvas the real <canvas> lives in a
 * persistent, position-fixed overlay (VisualizerDockShell), so a selector on
 * `.selection-main__image--<partId>` can no longer reach it.
 *
 * Note: this effect and DockedVisualizerWrapper's own `syncToViewport` RAF are two independent
 * authorities over the same transform, and syncToViewport's zero-duration `setTransform(x, y,
 * scale, 0)` calls are the one that should win while it's running — it's actively following the
 * dock shell's live, still-animating box. If this effect's own animated setTransform landed in
 * that same window, syncToViewport's per-frame instant jumps would repeatedly stomp the
 * in-progress easing, which could show as a visible snap (e.g. to plain cover-center) partway
 * through this effect's transition instead of one smooth move from wherever the canvas actually
 * was. `isSyncingViewportRef` (passed in from DockedVisualizerWrapper, true for the ~500ms that
 * RAF loop runs) lets this effect wait its turn: it defers its own setTransform call until that
 * ref goes false, then reads the *settled* position as the true starting point for its own
 * animation. See waitForViewportSyncToFinish above.
 *
 * @param transformRef the same ref passed to <TransformWrapper ref={...}>
 * @param canvasSize base (cover-scale) canvas size, from useCanvasSize
 * @param minScale the current cover-fit scale floor, from DockedVisualizerWrapper
 * @param isSyncingViewportRef
 *   DockedVisualizerWrapper's syncToViewport RAF loop is actively driving the transform (see
 *   above). Omit only if the caller doesn't have one to offer — this effect just won't wait for
 *   anything, no worse than before this ref existed.
 */
export function useZoomToActiveFeatureGroup(
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  canvasSize: CanvasSize,
  minScale: number,
  isSyncingViewportRef?: React.RefObject<boolean>,
): void {
  const dockState = useVisualizerStore(dockStateStore.subscribe, dockStateStore.getSnapshot);
  const activePartIdSelector = ACTIVE_PART_ID_SELECTOR_BY_SLOT[dockState.activeSlotName];
  const activePartId = useSelector((state: any) => (activePartIdSelector ? activePartIdSelector(state) : null));
  // console.log("activePartId", activePartId);

  // Only touch devices actually rotate; a desktop window that happens to cross the
  // square boundary keeps the old, immediate behavior.
  const isTouch = useAppSelector((state) => state.serverState.isTouch);

  // A real portrait<->landscape flip just happened (timestamp of the last one) — see the
  // ORIENTATION_* constants above for why this hook has to defer its re-frame in that case
  // instead of applying it when the deps change. Mirrors useResizingLoaderHook's own
  // orientation check (same "ignore same-orientation resizes, including the spurious one
  // browsers fire right after mount" rule) rather than depending on it, so the two stay
  // independent: that hook owns the remount, this one owns the re-framing.
  const orientationRef = useRef<Orientation>(getOrientation());
  const orientationFlipAtRef = useRef(0);
  const [orientationTick, setOrientationTick] = useState(0);
  useEffect(() => {
    const onResize = () => {
      const next = getOrientation();
      if (next === orientationRef.current) {
        return;
      }
      orientationRef.current = next;
      orientationFlipAtRef.current = Date.now();
      setOrientationTick((tick) => tick + 1);
    };

    window.addEventListener("resize", onResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onResize);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onResize);
      }
    };
  }, []);

  // Timestamp of the last dock-slot switch (route change), for the deferred re-frame described
  // in the DOCK_SWITCH_* block above. Derived during render on purpose, not in an effect: the
  // positioning effect below is declared last and must already see the new timestamp on the very
  // commit that changes the slot. Writing a ref during render is safe here — it's idempotent
  // (a re-render with the same slot is a no-op) and nothing renders off it.
  const lastSlotNameRef = useRef<string | null>(dockState.activeSlotName);
  const dockSwitchAtRef = useRef(0);
  if (lastSlotNameRef.current !== dockState.activeSlotName) {
    lastSlotNameRef.current = dockState.activeSlotName;
    dockSwitchAtRef.current = Date.now();
  }

  // Last partId this hook actually framed on the FULLSCREEN slot — see the guard at the top of
  // the positioning effect for why that slot needs one and the embedded slots don't.
  const lastFullscreenPartIdRef = useRef<string | null>(null);

  // False until this hook has framed the canvas once. The very first framing is applied with NO
  // animation; every one after it animates (FEATURE_ZOOM_ANIMATION_MS).
  //
  // The animation is there to show the user *that the canvas moved* in response to something they
  // did — picking a different feature group, switching room layout, opening the fullscreen
  // visualizer. On the first framing after a page load there is no "before" to move from: the
  // canvas has only just appeared, so a 1.3s glide from wherever syncToViewport's cover-fit
  // happened to leave it reads as the tool re-zooming itself for no reason (the complaint this
  // guards against on a fresh load of /materials). Landing straight on the right framing is both
  // faster and less noisy.
  //
  // Scope is deliberately "once per mounted hook" = once per app session, not once per slot
  // activation: DockedVisualizerWrapper is mounted exactly once for the app's lifetime (only the
  // <TransformWrapper> inside it ever remounts, via rwKey), so this stays false only until the
  // canvas is first framed. Navigating /visualizer -> /materials later still animates, which is
  // wanted — there the dock box itself visibly animates between slots and the framing move is part
  // of that same gesture.
  const hasFramedOnceRef = useRef(false);

  // getCurrentLayout().type (used for layout-specific overrides, see byLayoutType in
  // featureGroupZoomDefaults.ts) lives on a plain singleton, not Redux state, so switching room
  // layout doesn't otherwise re-run this hook. useDockedVisualizerInput.ts hits the same problem
  // for `layers` and solves it the same way: listen for the legacy CHANGE_LAYOUT event.
  //
  // CHANGE_LAYOUT is dispatched much more often than its name implies — e.g.
  // materialsWizardMain.js's relatedFeatureClick triggers it on every ordinary feature click, not
  // just on a true room-layout switch. So we can't treat "the event fired" as "the layout changed":
  // that previously re-ran the effect below on every feature click within the same group, which is
  // exactly the extra repositioning this hook should NOT do (setNewCanvasClass/materialsCanvasClass
  // itself already only updates at the wizard-step/group level, not per click — this tick was
  // undoing that). Instead, only bump the tick when layout.type actually changed since last seen.
  const layoutTypeRef = useRef(getCurrentLayout() && getCurrentLayout().type);
  const [layoutChangeTick, setLayoutChangeTick] = useState(0);
  useEffect(() => {
    const onLayoutChange = () => {
      const layout = getCurrentLayout();
      const newLayoutType = layout && layout.type;
      if (newLayoutType === layoutTypeRef.current) {
        return;
      }
      layoutTypeRef.current = newLayoutType;
      setLayoutChangeTick((tick) => tick + 1);
    };
    $(window).on(ActionTypes.CHANGE_LAYOUT, onLayoutChange);
    return () => {
      $(window).off(ActionTypes.CHANGE_LAYOUT, onLayoutChange);
    };
  }, []);

  useEffect(() => {
    // Only slots with a registered selector opt into this behavior (materials + fullscreen
    // visualizer) — leave every other docked slot (cart) exactly as syncToViewport left it.
    if (!activePartIdSelector) {
      return undefined;
    }

    // The fullscreen slot moves ONLY when its active feature group genuinely changes.
    //
    // The embedded slots want the opposite: /materials is entered with the canvas cover-centered
    // by syncToViewport, so re-framing on every re-run (slot switch, orientation flip, dock
    // resize) is what keeps the active group framed there. Fullscreen has no such reset to undo —
    // it's the canvas the user pans by hand, and every one of those re-runs (coming back from
    // /materials, an iOS URL-bar show/hide changing windowHeight, a room-layout switch) would
    // otherwise yank it out from under them. Framing on a real feature-group change only is the
    // whole point of the fix this guard belongs to.
    if (dockState.activeSlotName === DOCK_SLOTS.visualizer) {
      if (activePartId === lastFullscreenPartIdRef.current) {
        return undefined;
      }
      lastFullscreenPartIdRef.current = activePartId;
    }

    // Wait for any in-progress dock-transition sync to settle before touching the transform
    // ourselves — see the JSDoc above useZoomToActiveFeatureGroup and waitForViewportSyncToFinish.
    // Everything below only runs once isSyncingViewportRef reads false, so `ref.setTransform`'s
    // animation always starts from the real, settled position instead of racing syncToViewport's
    // own per-frame instant jumps.
    //
    // Gated behind USE_VIEWPORT_WAIT (see above) — when false, the zoom logic runs immediately
    // instead of deferring via waitForViewportSyncToFinish.
    const applyZoomToActiveFeatureGroup = (options?: ApplyOptions) => {
      const featureOnly = !!(options && options.featureOnly);
      const ref = transformRef.current;
      const instance = getVisualizerInstance();
      const canvasEl = instance && instance.canvas;
      if (!ref || !canvasEl) {
        return;
      }

      const viewport = getWrapperViewportRect(ref);
      if (!viewport) {
        return;
      }

      // On the fullscreen layout the mobile menu covers the right half of this same viewport, so
      // "centered" has to mean centered in what's left of it — otherwise every feature group we
      // frame lands behind the menu. Embedded slots get {0, 0} here (their dock box is already
      // the free area, and the menu isn't open), so this is a no-op for the materials wizard.
      const centerOffset = getOverlayCenterOffset(
        getWrapperElement(ref),
        document.querySelector(FULLSCREEN_OVERLAY_SELECTOR) as HTMLElement | null,
      );

      const visualizerType: string = document.kvConfig.visualizerType;
      const layout = getCurrentLayout();
      const layoutType: number | undefined = layout && layout.type;
      const featureGroup: any = activePartId && layout && layout.getFeatureGroupByPartId(activePartId);
      const clickedFeature: any = featureGroup && (featureGroup.activeFeature || featureGroup.activeNotInVisualizerFeature);
      const isMobile = Misc.isMobile() || isTouchDevice();
      const canZoomToFeature = clickedFeature && !clickedFeature.notAvailableInVisualizer;
      // On a very narrow canvas the feature-zoom scale does more harm than good: with less than
      // MIN_CANVAS_WIDTH_FOR_FEATURE_SCALE px of content width to work with, scaling up leaves the
      // framed group filling the whole viewport with no surrounding context. We still pan to the
      // feature group (the focal math below is unchanged) — we just do it at scale 1.
      const canScaleToFeature =
        shouldZoomPartId(activePartId, isMobile) && viewport && (viewport.width >= MIN_CANVAS_WIDTH_FOR_FEATURE_SCALE);

      const matchesIdleFraming =
        canZoomToFeature && getFeatureZoomMatchesIdle(visualizerType, activePartId as string, layoutType);

      const isIdleFraming = !canZoomToFeature || matchesIdleFraming;

      // Deferred dock-switch apply (featureOnly): only take over the transform when there is a real
      // feature group to frame. If this would just land on the idle/default position, leave the
      // canvas exactly where syncToViewport's cover-fit put it at the end of the slot transition —
      // re-stating the idle framing a beat after the page has already settled reads as an
      // unexplained drift, and it's the one case where doing nothing is visually correct. Every
      // other trigger (feature-group change, layout switch, orientation flip) still applies idle
      // framing normally, so leaving a stale feature zoom behind is not possible.
      if (featureOnly && isIdleFraming) {
        return;
      }

      // Instant for the first framing of the session, animated for every one after it — see
      // hasFramedOnceRef. Set here rather than at each setTransform call site so both branches
      // below (idle framing and feature zoom) count as "the canvas has now been framed once",
      // including the early `return` inside the feature branch when the bounding box can't be
      // resolved (that one leaves the canvas where syncToViewport put it, which is a perfectly
      // good first frame — the next framing should still animate). The featureOnly skip above is
      // deliberately ahead of it: a framing that never happened must not consume the first,
      // un-animated one.
      const animationMs = hasFramedOnceRef.current ? FEATURE_ZOOM_ANIMATION_MS : 0;
      hasFramedOnceRef.current = true;

      if (isIdleFraming) {
        // Nothing to frame (wizard step with no part selected yet, or a part that intentionally
        // isn't zoomed to, e.g. a wall) — or this part/layout is explicitly configured to match the
        // idle framing (matchIdle, see featureGroupZoomDefaults.ts) instead of zooming to its own
        // bounding-box center. Either way, rest at the per-visualizerType idle pan instead of
        // whatever the last feature-group zoom left behind.
        applyIdleFraming(ref, canvasSize, viewport, minScale, visualizerType, layoutType, centerOffset, animationMs);
        return;
      }

      // Raw, per-axis 0-100 percent along that axis's pan range — null on an axis means "not
      // configured for this part/layout", not "50". Each axis resolves independently below: an
      // entry with only offsetX still gets its Y position from the bounding-box center, and an
      // entry with only scale (both null) gets BOTH axes from the bounding-box center — same as no
      // entry at all.
      const offset = getFeatureZoomOffset(visualizerType, activePartId as string, layoutType);
      // Floor at minScale: several per-part overrides (see featureGroupZoomDefaults.ts) are only
      // slightly above 1 (e.g. bottom_cabinet at 1.1), which — in a narrow/short dock box — can be
      // less than the scale the canvas needs just to cover that box. Without this floor a low-scale
      // override could leave empty space around the canvas instead of merely "not zooming in much".
      const scale = canScaleToFeature
        ? Math.max(getFeatureZoomScale(visualizerType, activePartId as string, layoutType), minScale)
        : 1;

      // Only need the feature group's own bounding-box center for whichever axis isn't explicitly
      // configured above — skip the getFeatureGroupCommonCoords/getCoordsCenter walk (the expensive
      // part of this whole feature) entirely once both axes are configured.
      let boundingBoxCenter: any = null;
      if (offset.x === null || offset.y === null) {
        // getFeatureGroupCommonCoords caches its (otherwise expensive) result per featureGroup per
        // layout type, see its own JSDoc.
        const coords = getFeatureGroupCommonCoords(featureGroup, clickedFeature);
        if (!coords || !Object.keys(coords).length) {
          return;
        }
        boundingBoxCenter = getCoordsCenter(coords, canvasEl);
      }

      const focalX =
        offset.x !== null
          ? percentToFocalCoord(offset.x, canvasSize.width, viewport.width, scale)
          : boundingBoxCenter.x;
      const focalY =
        offset.y !== null
          ? percentToFocalCoord(offset.y, canvasSize.height, viewport.height, scale)
          : boundingBoxCenter.y;

      const { positionX, positionY } = computeFocalTransform({
        contentWidth: canvasSize.width,
        contentHeight: canvasSize.height,
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        scale,
        focalX,
        focalY,
        centerOffsetX: centerOffset.x,
        centerOffsetY: centerOffset.y,
      });

      ref.setTransform(positionX, positionY, scale, animationMs, FEATURE_ZOOM_EASING as any);
    };

    // Just rotated on a touch device, on the embedded materials canvas, with a feature group to
    // frame (materialsCanvasClass): the transform is about to be reset out from under us by the
    // TransformWrapper remount and by syncToViewport's cover-fit loop, so don't apply now — wait
    // until the flip has actually finished (ref back, sync loop idle) and frame then. See the
    // ORIENTATION_* block at the top of this file for the full ordering.
    const isOrientationReframe =
      isTouch &&
      !!activePartId &&
      ORIENTATION_REFRAME_SLOTS.indexOf(dockState.activeSlotName) !== -1 &&
      Date.now() - orientationFlipAtRef.current < ORIENTATION_REFRAME_WINDOW_MS;

    if (isOrientationReframe) {
      return waitForOrientationSettle(
        ORIENTATION_SETTLE_MS,
        () => !!transformRef.current && !(isSyncingViewportRef && isSyncingViewportRef.current),
        applyZoomToActiveFeatureGroup,
        ORIENTATION_REFRAME_TIMEOUT_MS,
      );
    }

    // Just switched dock slot (e.g. /shoppingcart -> /materials): the dock box is still the old
    // slot's, and syncToViewport is about to cover-fit the canvas every frame for the whole
    // ~500ms shell transition — so applying now frames the wrong viewport and then gets stomped
    // right back to cover-fit, which is the "zoom never runs after the page switch" symptom.
    // Frame once the switch has actually landed instead. See the DOCK_SWITCH_* block at the top.
    const isDockSwitchReframe =
      ENABLE_DOCK_SWITCH_REFRAME &&
      dockSwitchAtRef.current > 0 &&
      Date.now() - dockSwitchAtRef.current < DOCK_SWITCH_REFRAME_WINDOW_MS;

    if (isDockSwitchReframe) {
      return waitForDockSwitchToSettle(
        isSyncingViewportRef,
        () => !!transformRef.current,
        // featureOnly: a page switch that lands on the default/idle framing leaves the canvas as
        // syncToViewport cover-fit it — only a real feature group is worth moving for. See
        // ApplyOptions.
        () => applyZoomToActiveFeatureGroup({ featureOnly: true }),
        DOCK_SWITCH_SYNC_START_GRACE_MS,
        DOCK_SWITCH_REFRAME_TIMEOUT_MS,
      );
    }

    if (USE_VIEWPORT_WAIT) {
      return waitForViewportSyncToFinish(isSyncingViewportRef, applyZoomToActiveFeatureGroup);
    }

    applyZoomToActiveFeatureGroup();
    return undefined;
    // Re-run on every activePartId change (wizard step / active feature group moving to a new
    // group — see materialsWizardMain.js's setNewCanvasClass, which only updates
    // materialsCanvasClass at that same granularity), every genuine layoutChangeTick bump (a real
    // room-layout switch, since overrides can be layout-specific — see featureGroupZoomDefaults.ts's
    // byLayoutType), and whenever canvasSize/minScale change (orientation flip, dock resize) — but
    // NOT on every click of an individual feature within the same still-active group, so a stale
    // scale/offset pair from before is never left applied without also re-framing on every click.
    //
    // orientationTick additionally re-runs it the moment a portrait<->landscape flip starts, which
    // is what opens the deferred-re-frame window above: on a touch device the canvasSize change
    // that a flip eventually produces can land either before or after the TransformWrapper remount,
    // so we can't rely on it alone to be the trigger that ends up doing the framing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dockState.activeSlotName,
    activePartIdSelector,
    activePartId,
    layoutChangeTick,
    orientationTick,
    isTouch,
    canvasSize.width,
    canvasSize.height,
    minScale,
  ]);
}

export default useZoomToActiveFeatureGroup;
